import { useState, useCallback, useEffect } from 'react';
import { SocialMediaPost, SocialPostStatus } from '@/lib/api';
import { socialMediaApi } from '../api';
import { toast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

/**
 * Lists posts for a Holder with pagination + status/search filters.
 * Stays idle while no Holder is selected; the page that owns this
 * hook is responsible for rendering the workspace-picker prompt.
 */
export function useSocialPosts(
  holderId: string | null,
  statusFilter?: SocialPostStatus,
  search?: string,
) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const loadPosts = useCallback(
    async (targetPage: number) => {
      if (!holderId) {
        setPosts([]);
        setTotal(0);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await socialMediaApi.listSocialMediaPosts(holderId, {
          status: statusFilter,
          search: search || undefined,
          limit: PAGE_SIZE,
          offset: targetPage * PAGE_SIZE,
        });
        setPosts(result.posts);
        setTotal(result.total);
        setPage(targetPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load posts');
      } finally {
        setIsLoading(false);
      }
    },
    [holderId, statusFilter, search],
  );

  useEffect(() => {
    loadPosts(0);
  }, [loadPosts]);

  const goToNextPage = useCallback(() => {
    const nextPage = page + 1;
    if (nextPage * PAGE_SIZE < total) {
      loadPosts(nextPage);
    }
  }, [page, total, loadPosts]);

  const goToPrevPage = useCallback(() => {
    if (page > 0) {
      loadPosts(page - 1);
    }
  }, [page, loadPosts]);

  const deletePost = useCallback(
    async (postId: string) => {
      if (!holderId) return;
      try {
        await socialMediaApi.deleteSocialMediaPost(holderId, postId);
        setTotal((prev) => prev - 1);
        // Reload current page to keep consistent pagination
        loadPosts(page);
        toast({ title: 'Post deleted' });
        navigate('/a/arche_social_media');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete post';
        toast({ title: 'Error', description: message, variant: 'destructive' });
      }
    },
    [holderId, page, loadPosts, navigate],
  );

  const hasPrevPage = page > 0;
  const hasNextPage = (page + 1) * PAGE_SIZE < total;
  const firstItem = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastItem = Math.min((page + 1) * PAGE_SIZE, total);

  return {
    posts,
    total,
    isLoading,
    error,
    loadPosts,
    page,
    hasPrevPage,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
    firstItem,
    lastItem,
    deletePost,
  };
}
